import type { Locale } from './i18n';

/** Sign-in messages (German master, English 1.19). */
export const AUTH_TEXT = {
  de: {
    wrong:
      'E-Mail oder Passwort stimmt nicht. Tippe auf „Passwort vergessen?“, wenn du unsicher bist.',
    exists: 'Mit dieser E-Mail gibt es schon ein Konto – melde dich einfach an.',
    weak: 'Das Passwort ist zu kurz. Bitte mindestens 8 Zeichen.',
    email: 'Diese E-Mail-Adresse sieht nicht richtig aus.',
    disabled: 'Dieses Konto ist gesperrt. Bitte melde dich bei uns.',
    tooMany: 'Kurz durchatmen – zu viele Versuche. Bitte in ein paar Minuten nochmal.',
    offline: 'Keine Verbindung. Prüf kurz das WLAN und versuch es nochmal.',
    link: 'Der Link ist abgelaufen oder wurde schon benutzt. Fordere einfach einen neuen an.',
    notHere: 'Diese Anmeldung ist hier gerade nicht möglich. Bitte mit Google anmelden.',
    other: 'Das hat nicht geklappt. Bitte nochmal versuchen.',
  },
  en: {
    wrong: 'Email or password is wrong. Tap “Forgot password?” if you’re not sure.',
    exists: 'There’s already an account with this email – just sign in.',
    weak: 'The password is too short. Please use at least 8 characters.',
    email: 'This email address doesn’t look right.',
    disabled: 'This account is locked. Please get in touch with us.',
    tooMany: 'Take a breather – too many attempts. Please try again in a few minutes.',
    offline: 'No connection. Check your Wi-Fi and try again.',
    link: 'This link has expired or was already used. Just ask for a new one.',
    notHere: 'This sign-in isn’t possible here right now. Please sign in with Google.',
    other: 'That didn’t work. Please try again.',
  },
} as const;

/**
 * Firebase Auth error codes → what a family member should read. Kept pure (code in, text out)
 * so it is unit-tested; the web app only passes `error.code`. Undefined means "say nothing"
 * (the person closed the Google popup themselves).
 */
export function authMessage(code: string, locale: Locale = 'de'): string | undefined {
  const t = AUTH_TEXT[locale];
  const c = code.replace(/^auth\//, '');
  switch (c) {
    case 'popup-closed-by-user':
    case 'cancelled-popup-request':
      return undefined;
    case 'invalid-credential':
    case 'invalid-login-credentials':
    case 'wrong-password':
    case 'user-not-found':
      return t.wrong;
    case 'email-already-in-use':
      return t.exists;
    case 'weak-password':
    case 'password-does-not-meet-requirements':
      return t.weak;
    case 'invalid-email':
    case 'missing-email':
      return t.email;
    case 'user-disabled':
      return t.disabled;
    case 'too-many-requests':
      return t.tooMany;
    case 'network-request-failed':
      return t.offline;
    case 'expired-action-code':
    case 'invalid-action-code':
      return t.link;
    case 'operation-not-allowed':
    case 'unauthorized-continue-uri':
    case 'unauthorized-domain':
      // a preview address that is not allowed, or the method is switched off
      return t.notHere;
    default:
      return t.other;
  }
}
